/**
 * Named entry points for reporting an error to Sentry, so no call site touches
 * `window.Sentry` directly. Every function is a no-op when the Loader is absent
 * — unconfigured env, a blocked CDN, or server rendering — because reporting a
 * failure must never become a second failure.
 */
import { isExpectedClientErrorOutcome } from '@kiao-voice/core'
import { getOrCreateDeviceId } from '../identity/device-id'
import { reportUnlinkedException } from './report-unlinked-exception'

/**
 * The subset of the Sentry API the LOADER guarantees before the real SDK has
 * finished downloading. Calls made through the stub are buffered and replayed,
 * so everything here is safe from the very first paint; anything outside this
 * list would be `undefined` until the SDK lands and must be wrapped in
 * `Sentry.onLoad()`.
 *
 * Typed locally rather than imported: apps/web deliberately has no `@sentry/*`
 * dependency — the whole point of the Loader is that the SDK never enters the
 * bundle.
 */
interface SentryLoaderApi {
  captureException?: (exception: unknown, context?: SentryCaptureContext) => void
  captureMessage?: (message: string, context?: SentryCaptureContext) => void
  addBreadcrumb?: (breadcrumb: Record<string, unknown>) => void
  forceLoad?: () => void
}

interface SentryCaptureContext {
  tags?: Record<string, string | number | boolean>
  fingerprint?: string[]
  // value-list: local — Sentry SDK 的 SeverityLevel，照 SDK 型別抄的第三方值；apps/web 刻意不依賴 @sentry/*
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
}

/** Scalars only — everything here becomes a searchable Sentry tag. */
export type ReportContext = Record<string, string | number | boolean | undefined>

export interface TrialUploadFailureReport {
  /** The classified failure bucket; also the second half of the fingerprint. */
  reason: string
  /** HTTP status, when the failure got one at all. */
  status?: number
  /** `AnonymousTrialErrorCode`, when the error was an API error. */
  code?: string
  /** Which leg of the upload failed: `initiate` / `put` / `complete`. */
  phase?: string
  cause: unknown
}

export interface MemberUploadFailureReport {
  /** Sanitized transport leg; never a URL, filename, or object key. */
  phase: string
  status?: number
  code?: string
  /** Whether a direct-to-R2 attempt already failed before the proxy leg failed. */
  directFallback?: boolean
  cause: unknown
}

function sentry(): SentryLoaderApi | null {
  if (typeof window === 'undefined') return null
  const loaded = (window as unknown as { Sentry?: SentryLoaderApi }).Sentry
  return loaded ?? null
}

/**
 * Tags every report carries. `kv_did` is the opaque first-party device id — the
 * SAME value `anonymous_events` is keyed by, which is what lets support pivot
 * from a Sentry issue to what that browser did before and after. It is never an
 * address or a name; no `Sentry.setUser` is called anywhere in apps/web.
 */
function baseTags(): Record<string, string> {
  try {
    const deviceId = getOrCreateDeviceId()
    return deviceId ? { kv_did: deviceId } : {}
  } catch (cause) {
    // Some sandboxed/private contexts can reject even a cookie read. Report
    // that independently, then preserve the original event without kv_did.
    reportUnlinkedException(cause, 'lib/error-reporting/report.ts')
    return {}
  }
}

function definedTags(context: ReportContext): Record<string, string | number | boolean> {
  const tags: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(context)) {
    if (value !== undefined) tags[key] = value
  }
  return tags
}

/**
 * A `(status, code)` pair in `EXPECTED_CLIENT_ERROR_OUTCOMES`: an outcome the
 * caller already shows as a normal screen (the daily trial cap, a self-healing
 * active context). Every reporter checks this, so a dedicated upload channel
 * cannot turn one back into an error.
 */
function isExpectedOutcome(status: number | null, code: string | null): boolean {
  return status !== null && isExpectedClientErrorOutcome(status, code)
}

/**
 * For calls made with a session, a 401 is also expected: the session ended and
 * the caller shows the sign-in prompt. The guest trial needs no session, so a
 * 401 there means something in front of the API is wrong and stays an error.
 */
function isExpectedSignedInOutcome(status: number | null, code: string | null): boolean {
  return status === 401 || isExpectedOutcome(status, code)
}

/**
 * Kept as a breadcrumb, so a later real error on the same page still shows it.
 */
function recordExpectedOutcome(data: ReportContext): void {
  sentry()?.addBreadcrumb?.({
    category: 'handled.expected',
    level: 'info',
    data: definedTags(data),
  })
}

/**
 * A trial upload that never reached the result page.
 *
 * This is the second of the two channels the issue asks for. The first —
 * mirroring `trial_upload_failed` into `anonymous_events` — travels our own API
 * origin, the very path that just failed, so the failures that matter most
 * (network drop, a proxy or Cloudflare eating the API host) are exactly the ones
 * it cannot carry. sentry.io is a different destination on a different route.
 *
 * Grouped by `reason` rather than by stack: five browsers hitting the same 502
 * are one problem, and a network drop is a different one regardless of which
 * line threw.
 *
 * An expected outcome (the daily trial cap) is only a breadcrumb here: the API
 * answered, so `anonymous_events` already has it and is where to count it.
 */
export function reportTrialUploadFailure(report: TrialUploadFailureReport): void {
  if (isExpectedOutcome(report.status ?? null, report.code ?? null)) {
    recordExpectedOutcome({
      report_type: 'trial_upload_failed',
      status: report.status,
      code: report.code,
      phase: report.phase,
    })
    return
  }
  sentry()?.captureException?.(report.cause, {
    tags: {
      report_type: 'trial_upload_failed',
      ...definedTags({
        reason: report.reason,
        status: report.status,
        code: report.code,
        phase: report.phase,
      }),
      ...baseTags(),
    },
    fingerprint: ['trial_upload_failed', report.reason],
  })
}

/**
 * A signed-in transcription upload that did not create a usable batch.
 *
 * Member uploads used to stop at `console.error`, which is deliberately
 * excluded from Sentry breadcrumbs. That made browser-only failures (R2 CORS,
 * blocked presigned hosts, or a network drop before the API request) invisible
 * to operators. Keep this payload to coarse transport metadata: the exception
 * plus phase/status/code are enough to diagnose it, while filenames, signed
 * URLs, object keys, and account data never enter the report.
 */
export function reportMemberUploadFailure(report: MemberUploadFailureReport): void {
  if (
    (report.cause instanceof Error || report.cause instanceof DOMException) &&
    report.cause.name === 'AbortError'
  ) {
    return
  }
  if (isExpectedSignedInOutcome(report.status ?? null, report.code ?? null)) {
    recordExpectedOutcome({
      report_type: 'member_upload_failed',
      status: report.status,
      code: report.code,
      phase: report.phase,
    })
    return
  }
  const group =
    report.code ?? (report.status ? `http_${Math.floor(report.status / 100)}xx` : 'network')
  sentry()?.captureException?.(report.cause, {
    tags: {
      report_type: 'member_upload_failed',
      ...definedTags({
        phase: report.phase,
        status: report.status,
        code: report.code,
        direct_fallback: report.directFallback,
      }),
      ...baseTags(),
    },
    fingerprint: ['member_upload_failed', report.phase, group],
  })
}

/**
 * An exception that the product deliberately recovered from with fallback UI.
 * Reporting it is the price of catching it: users keep the better recovery,
 * while operators still see the failure instead of inheriting a silent blind
 * spot.
 *
 * Classified before anything is sent:
 * - AbortError is dropped: an intentional user/navigation cancellation.
 * - A browser network failure goes out at warning level, one issue per call site.
 * - A 401, or a `(status, code)` pair in `EXPECTED_CLIENT_ERROR_OUTCOMES`, is an
 *   outcome the caller already shows as a normal screen. It becomes a
 *   breadcrumb, so a later real error on the same page still shows it.
 * - Where money moves (`MONEY_PATH_SOURCE_ROOTS`) neither downgrade applies: a
 *   checkout that fails for any reason, network included, is a lost payment
 *   and is always an error.
 * - Everything else is an error. With a `source`, it is grouped by call site,
 *   error name, status and code, so a truncated Safari stack or a moved line
 *   does not split one cause into several issues.
 *
 * Call sites cannot filter for themselves: `handled-error-guard.test.ts`
 * requires every catch to call a reporter first.
 */
export function reportHandledException(cause: unknown, context: ReportContext = {}): void {
  if ((cause instanceof Error || cause instanceof DOMException) && cause.name === 'AbortError') {
    return
  }
  const source = typeof context.source === 'string' ? context.source : undefined
  // Built only once a report is really sent: resolving the device id writes a
  // cookie, which must not happen when there is no Sentry on the page.
  const tags = () => ({ report_type: 'handled_exception', ...definedTags(context), ...baseTags() })

  const moneyPath = isMoneyPathSource(source)

  if (!moneyPath && isBrowserNetworkFailure(cause)) {
    sentry()?.captureException?.(cause, {
      tags: tags(),
      level: 'warning',
      fingerprint: ['network_error', source ?? '<none>'],
    })
    return
  }

  const status = statusOf(cause)
  const code = codeOf(cause)
  if (!moneyPath && isExpectedSignedInOutcome(status, code)) {
    recordExpectedOutcome({ status: status ?? undefined, code: code ?? undefined, source })
    return
  }

  sentry()?.captureException?.(cause, {
    tags: tags(),
    ...(source === undefined
      ? {}
      : {
          fingerprint: ['handled', source, nameOf(cause), String(status ?? 'none'), code ?? 'none'],
        }),
  })
}

/**
 * Call sites where money moves (a directory, or one file outside it): every
 * failure there is reported as an error. `money-path-guard.test.ts` fails when
 * a file that calls a payment API reports from a source not listed here.
 */
const MONEY_PATH_SOURCE_ROOTS = [
  'features/billing/',
  'features/payment/',
  'features/orders-admin/',
  'features/team/team-subscribe-modal.tsx',
  'features/team/trial-seat-section.tsx',
  'features/team/seat-section.tsx',
  'features/team/add-member-section.tsx',
  'app/app/account/billing/',
  'app/(public)/(marketing)/billing/',
  'app/(public)/(marketing)/pricing/',
] as const

export function isMoneyPathSource(source: string | undefined): boolean {
  return source !== undefined && MONEY_PATH_SOURCE_ROOTS.some((root) => source.startsWith(root))
}

/**
 * What each browser's `fetch` rejects with when the request never got a
 * response: Chrome (which may append ` (<host>)`), Safari, Firefox, and the
 * React Native polyfill. A CORS rejection looks identical, so these are still
 * reported, grouped per call site at warning level.
 */
const BROWSER_NETWORK_FAILURE_MESSAGES = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
  'Network request failed',
] as const

function isBrowserNetworkFailure(cause: unknown): boolean {
  if (!(cause instanceof TypeError)) return false
  return BROWSER_NETWORK_FAILURE_MESSAGES.some(
    (message) => cause.message === message || cause.message.startsWith(`${message} (`)
  )
}

/** The HTTP status the error classes carry as a numeric `status` field. */
function statusOf(cause: unknown): number | null {
  if (!cause || typeof cause !== 'object') return null
  const status = (cause as { status?: unknown }).status
  return typeof status === 'number' ? status : null
}

/** Machine codes only: an English sentence in the same field is not a code. */
const ERROR_CODE_PATTERN = /^[a-z0-9_]+$/

/**
 * The API's `{ error }` code: the feature error classes lift it to `code`, the
 * shared `ApiError` keeps it in `body.error`.
 */
function codeOf(cause: unknown): string | null {
  if (!cause || typeof cause !== 'object') return null
  const lifted = (cause as { code?: unknown }).code
  const body = (cause as { body?: unknown }).body
  const raw =
    lifted !== undefined && lifted !== null
      ? lifted
      : body && typeof body === 'object'
        ? (body as { error?: unknown }).error
        : undefined
  return typeof raw === 'string' && ERROR_CODE_PATTERN.test(raw) ? raw : null
}

function nameOf(cause: unknown): string {
  if (cause && typeof cause === 'object') {
    const name = (cause as { name?: unknown }).name
    if (typeof name === 'string' && name !== '') return name
  }
  return 'unknown'
}

/**
 * Any other web exception worth a look. Provided so call sites that are not a
 * deliberate recovery path can still use the same privacy-safe tag handling.
 */
export function reportWebException(cause: unknown, context: ReportContext = {}): void {
  sentry()?.captureException?.(cause, {
    tags: { ...definedTags(context), ...baseTags() },
  })
}

/** How often to re-check for the Loader, and how long to keep looking. */
const PRELOAD_POLL_INTERVAL_MS = 100
const PRELOAD_TIMEOUT_MS = 10_000

/**
 * Ask the dormant Loader to fetch the real SDK now. Returns a cancel function
 * for the caller's effect cleanup.
 *
 * Only the pages where people DO things call this (see `<ErrorReportingPreload>`).
 * Without it the SDK arrives only once an error has already happened, so the
 * first error of a session has no breadcrumbs — the requests and clicks that led
 * to it. Marketing pages accept that trade: they are LCP-critical and nearly
 * interaction-free, so the error itself, its stack and its URL are enough.
 *
 * It POLLS rather than calling `forceLoad` once, because of an ordering that was
 * measured in a browser rather than assumed: the Loader is a `next/script`
 * `afterInteractive` tag, so React has already run this effect by the time the
 * tag is injected — `window.Sentry` is still `undefined`, a single attempt finds
 * nothing, and the SDK would stay dormant for the whole session on exactly the
 * pages that asked for it eagerly. Polling stops the moment it succeeds, and
 * gives up after {@link PRELOAD_TIMEOUT_MS} so a blocked CDN cannot leave a
 * timer running for the rest of the visit.
 */
export function preloadErrorReporting(): () => void {
  if (typeof window === 'undefined') return () => {}

  const forceLoadNow = (): boolean => {
    const api = sentry()
    if (!api?.forceLoad) return false
    api.forceLoad()
    return true
  }

  if (forceLoadNow()) return () => {}

  const deadline = Date.now() + PRELOAD_TIMEOUT_MS
  const timer = window.setInterval(() => {
    if (forceLoadNow() || Date.now() >= deadline) window.clearInterval(timer)
  }, PRELOAD_POLL_INTERVAL_MS)

  return () => window.clearInterval(timer)
}
